import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { createContact, updateContact } from "../smsApi";
import { formatPhoneInput, toE164 } from "../phone";
import type { SmsContact, SmsContactListType } from "../types";

const LIST_TYPES: SmsContactListType[] = ["Employee", "Customer", "Member", "Other"];

type Props = {
  contact: SmsContact;
  mode?: "create" | "edit";
  onClose: () => void;
  onSaved: (contact: SmsContact) => void;
};

export function ContactModal({ contact, mode, onClose, onSaved }: Props) {
  // `mode` lets callers force "create" even when passing a seed contact
  // with a phone_number (used from the thread header's "+ Add Contact").
  const isEditing = mode ? mode === "edit" : !!contact.id;
  const [name, setName] = useState(contact.name ?? "");
  const [phone, setPhone] = useState("");
  const [listType, setListType] = useState<SmsContactListType>(
    contact.list_type ?? "Customer"
  );
  const [notes, setNotes] = useState(contact.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (contact.phone_number) {
      const digits = contact.phone_number.replace(/\D/g, "").slice(-10);
      setPhone(formatPhoneInput(digits));
    }
  }, [contact.phone_number]);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  async function handleSave() {
    setError(null);
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    const e164 = toE164(phone);
    if (!e164) {
      setError("Enter a valid 10-digit phone number");
      return;
    }

    setSaving(true);
    try {
      const saved = isEditing
        ? await updateContact(contact.id, {
            name: name.trim(),
            phone_number: e164,
            list_type: listType,
            notes: notes.trim() || null,
          })
        : await createContact({
            name: name.trim(),
            phone_number: e164,
            list_type: listType,
            notes: notes.trim() || null,
          });
      onSaved(saved);
      onClose();
    } catch (e) {
      const msg = (e as Error).message;
      // Worker returns "contact_exists" when unique phone_number is violated.
      if (msg.includes("contact_exists")) {
        setError("A contact with this number already exists");
      } else if (msg.includes("admin_only")) {
        setError("Only admins can add or edit contacts");
      } else {
        setError(msg || "Failed to save contact");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md rounded-xl border border-tribal-border bg-tribal-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-tribal-border px-5 py-4">
          <h2 className="text-lg font-semibold text-white">
            {isEditing ? "Edit Contact" : "Add Contact"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-tribal-muted transition-colors hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              Name
            </label>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Smith"
              className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              Phone Number
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
              placeholder="(555) 123-4567"
              className="w-full rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              List Type
            </label>
            <div className="flex flex-wrap gap-2">
              {LIST_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setListType(type)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    listType === type
                      ? "bg-tribal-red text-white"
                      : "bg-tribal-border text-tribal-muted hover:text-white"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-tribal-muted">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes…"
              rows={2}
              className="w-full resize-none rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
            />
          </div>

          {error && (
            <p className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-tribal-border px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-tribal-muted transition-colors hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-tribal-red px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark disabled:opacity-50"
          >
            {saving ? "Saving…" : isEditing ? "Save Changes" : "Add Contact"}
          </button>
        </div>
      </div>
    </div>
  );
}
